import * as React from "react";
import { useEffect } from "react";
import { IS_CART_OPEN } from "@/helpers/constants";
import { useCartUiActions } from "@/features/cart";
import { track } from "@/helpers/googleAnalytics";
import { shopifyCartApi } from "@/services/shopifyCart";

// Due to the possibility of there being forms managed outside of react,
// we hook into the global event submit listener for all cart adds
const useOpenCartDrawerOnCartAdd = () => {
  const { openDrawer } = useCartUiActions();
  const addItemToCart = shopifyCartApi.useAdd();

  useEffect(() => {
    function handleFormSubmission(ev: SubmitEvent) {
      if (ev.defaultPrevented || !(ev.target instanceof HTMLFormElement)) {
        return;
      }

      if (ev.target.action.endsWith("/cart/add")) {
        ev.preventDefault();
        addItemToCart.mutate(new FormData(ev.target), {
          onSuccess: () => openDrawer(),
        });
      }
    }

    window.addEventListener("submit", handleFormSubmission);
    return () => {
      window.removeEventListener("submit", handleFormSubmission);
    };
  }, [openDrawer, addItemToCart]);
};

const useCartUrls = () => {
  const { openDrawer } = useCartUiActions();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get(IS_CART_OPEN) === "true") {
      openDrawer();
    } else if (window.location.pathname === "/cart") {
      history.replaceState("", "", "/");
      openDrawer();
    }
  }, [openDrawer]);
};

const useTotals = () => {
  const totalPrice = shopifyCartApi.useFetch().data?.items_subtotal_price;
  useEffect(() => {
    if (totalPrice) {
      track({ name: "Update Cart Total", total: totalPrice });
    }
  }, [totalPrice]);
};

type CartProviderProps = {
  children: React.ReactNode;
};

export const CartProvider = ({ children }: CartProviderProps) => {
  useOpenCartDrawerOnCartAdd();
  useCartUrls();
  useTotals();
  return <>{children}</>;
};
